import { z } from "zod";
import { AgentId, ConversationId, idSchema, LocalizedText, MessageId, ProjectId, TaskId, TemplateId, TenantId, Timestamp, UserId } from "./common.js";
import { Actor, Ref } from "./entities.js";

/**
 * 协作层实体（P2–P6）：任务、仓库/PR、邮件、会议、资产、用量、市场。
 * 由集成者在 P1 期间预先定稿，各阶段开工前如需调整再冻结一次。
 */

export const RepoId = idSchema("repo");
export type RepoId = z.infer<typeof RepoId>;
export const ListingId = idSchema("listing");
export const RentalId = idSchema("rental");
export const MediaJobId = idSchema("mediaJob");
export const MeetingId = idSchema("meeting");
export type MeetingId = z.infer<typeof MeetingId>;
export const AssetId = idSchema("asset");
export type AssetId = z.infer<typeof AssetId>;

// ───────────── 任务（A2A 9 态） ─────────────

export const TaskStatus = z.enum([
  "submitted",
  "working",
  "input_required",
  "auth_required",
  "completed",
  "failed",
  "canceled",
  "rejected",
  "unknown",
]);
export type TaskStatus = z.infer<typeof TaskStatus>;

/** 合法状态转移（bus 强制） */
export const TASK_TRANSITIONS: Record<TaskStatus, TaskStatus[]> = {
  submitted: ["working", "rejected", "canceled"],
  working: ["input_required", "auth_required", "completed", "failed", "canceled"],
  input_required: ["working", "canceled", "failed"],
  auth_required: ["working", "canceled", "failed"],
  completed: [],
  failed: [],
  canceled: [],
  rejected: [],
  unknown: ["submitted", "working"],
};

export const Task = z.object({
  id: TaskId,
  projectId: ProjectId,
  parentId: TaskId.nullable(),
  title: z.string().min(1).max(200),
  spec: z.string().max(20000).default(""),
  status: TaskStatus,
  assignee: AgentId.nullable(),
  createdBy: Actor,
  /** 分支 agent/<agentName>/<taskId> */
  branch: z.string().nullable(),
  prNumber: z.number().int().positive().nullable(),
  artifacts: z.array(Ref).default([]),
  /** 返工时引用原任务 */
  referenceTaskIds: z.array(TaskId).default([]),
  blockedReason: z.string().nullable(),
  confirmedAt: Timestamp.nullable(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Task = z.infer<typeof Task>;

// ───────────── Git（Gitea 代理） ─────────────

export const Repo = z.object({
  id: RepoId,
  projectId: ProjectId,
  name: z.string().regex(/^[a-zA-Z0-9._-]{1,64}$/),
  defaultBranch: z.string().default("main"),
  cloneUrl: z.string(),
  webUrl: z.string().nullable(),
  createdAt: Timestamp,
});
export type Repo = z.infer<typeof Repo>;

export const PullState = z.enum(["open", "merged", "closed"]);
export const PullRequest = z.object({
  repoId: RepoId,
  number: z.number().int().positive(),
  title: z.string(),
  body: z.string(),
  head: z.string(),
  base: z.string(),
  state: PullState,
  author: Actor,
  taskId: TaskId.nullable(),
  mergeable: z.boolean().nullable(),
  reviews: z.array(z.object({ by: Actor, event: z.enum(["approve", "request_changes", "comment"]), body: z.string(), at: Timestamp })).default([]),
  additions: z.number().int().nonnegative(),
  deletions: z.number().int().nonnegative(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type PullRequest = z.infer<typeof PullRequest>;

// ───────────── 邮件（mail_thread 会话 + 元数据） ─────────────

export const MailPriority = z.enum(["low", "normal", "high", "urgent"]);
export type MailPriority = z.infer<typeof MailPriority>;

export const Mail = z.object({
  id: MessageId,
  threadId: ConversationId,
  from: Actor,
  to: z.array(AgentId).min(1),
  subject: z.string().max(200),
  body: z.string().max(50000),
  attachments: z.array(Ref).default([]),
  priority: MailPriority,
  readBy: z.array(AgentId).default([]),
  createdAt: Timestamp,
});
export type Mail = z.infer<typeof Mail>;

// ───────────── 会议 ─────────────

export const MeetingState = z.enum(["created", "running", "summarizing", "closed"]);
export const Meeting = z.object({
  id: MeetingId,
  projectId: ProjectId,
  conversationId: ConversationId,
  title: z.string().max(200),
  agenda: z.array(z.string()).min(1),
  moderator: Actor,
  participants: z.array(AgentId).min(1),
  state: MeetingState,
  round: z.number().int().nonnegative(),
  maxRounds: z.number().int().positive().max(20),
  currentSpeaker: AgentId.nullable(),
  budgetMicroUsd: z.number().int().nonnegative().nullable(),
  minutes: z
    .object({ summary: z.string(), decisions: z.array(z.string()), actionItems: z.array(z.object({ title: z.string(), assignee: AgentId.nullable(), taskId: TaskId.nullable() })) })
    .nullable(),
  createdAt: Timestamp,
});
export type Meeting = z.infer<typeof Meeting>;

// ───────────── 资产 ─────────────

export const AssetKind = z.enum(["image", "model3d", "audio", "sprite", "other"]);

/** 3D 模型版本的结构化指标（入库时由服务端解析 GLB 得到；非 GLB 为 null） */
export const Model3dMeta = z.object({
  format: z.enum(["glb", "gltf", "fbx", "obj", "usdz", "other"]),
  tris: z.number().int().nonnegative(),
  verts: z.number().int().nonnegative(),
  meshes: z.number().int().nonnegative(),
  materials: z.number().int().nonnegative(),
  textures: z.array(z.object({ width: z.number().int(), height: z.number().int(), mimeType: z.string() })),
  /** 动画片段名 */
  animations: z.array(z.string()),
  /** 是否带骨骼蒙皮 */
  skinned: z.boolean(),
  joints: z.number().int().nonnegative(),
  /** 包围盒尺寸（米，已考虑节点变换） */
  bbox: z.object({ x: z.number(), y: z.number(), z: z.number() }),
  /** glTF 校验结果；未校验为 null */
  validation: z.object({ errors: z.number().int(), warnings: z.number().int() }).nullable(),
});
export type Model3dMeta = z.infer<typeof Model3dMeta>;

/** 3D 媒体操作 */
export const Model3dOp = z.enum(["generate", "remesh", "retexture", "rig", "animate", "convert"]);
export type Model3dOp = z.infer<typeof Model3dOp>;

export const AssetVersion = z.object({
  version: z.number().int().positive(),
  storageKey: z.string(),
  mediaType: z.string(),
  size: z.number().int().nonnegative(),
  prompt: z.string().nullable(),
  provider: z.string().nullable(),
  params: z.record(z.string(), z.unknown()).default({}),
  costMicroUsd: z.number().int().nonnegative().default(0),
  derivedFrom: z.object({ assetId: AssetId, version: z.number().int() }).nullable(),
  /** 仅 model3d：结构化指标 */
  model: Model3dMeta.nullable().optional(),
  /** 是否有缩略图（图片由服务端生成；模型来自供应商的预览图或 agent 上传的转台图）。download?variant=thumb 可用 */
  hasThumb: z.boolean().optional(),
  createdBy: Actor,
  createdAt: Timestamp,
});
export const Asset = z.object({
  id: AssetId,
  projectId: ProjectId,
  kind: AssetKind,
  name: z.string().max(200),
  tags: z.array(z.string()).default([]),
  latestVersion: z.number().int().positive(),
  /** 最新版本是否有缩略图（列表用，避免逐个试取） */
  hasThumb: z.boolean().optional(),
  versions: z.array(AssetVersion).optional(),
  publishedPath: z.string().nullable(),
  rating: z.number().min(0).max(5).nullable(),
  createdAt: Timestamp,
});
export type Asset = z.infer<typeof Asset>;

export const MediaJobState = z.enum(["queued", "running", "succeeded", "failed"]);
export const MediaJob = z.object({
  jobId: MediaJobId,
  projectId: ProjectId,
  requestedBy: AgentId,
  kind: z.enum(["image", "model3d"]),
  /** 3D 任务的操作类型；缺省 = generate */
  op: Model3dOp.optional(),
  provider: z.string(),
  state: MediaJobState,
  /** 供应商报告的进度 0–100；未知为 null */
  progress: z.number().min(0).max(100).nullable().optional(),
  /** 基于哪个资产版本处理（remesh / retexture / rig / animate / convert） */
  source: z.object({ assetId: AssetId, version: z.number().int() }).nullable().optional(),
  assetIds: z.array(AssetId).default([]),
  error: z.string().nullable(),
  estCostMicroUsd: z.number().int().nonnegative(),
  createdAt: Timestamp,
});
export type MediaJob = z.infer<typeof MediaJob>;

// ───────────── 用量 / 计费 / 市场 ─────────────

export const UsageKind = z.enum(["llm", "media", "compute"]);
export const UsageRecord = z.object({
  tenantId: TenantId,
  projectId: ProjectId.nullable(),
  agentId: AgentId.nullable(),
  kind: UsageKind,
  amount: z.number(),
  unit: z.string(),
  costMicroUsd: z.number().int().nonnegative(),
  billable: z.boolean(),
  at: Timestamp,
});
export type UsageRecord = z.infer<typeof UsageRecord>;

export const Currency = z.enum(["USD", "CNY"]);
export const Wallet = z.object({ tenantId: TenantId, balanceMicroUsd: z.number().int(), currency: Currency, updatedAt: Timestamp });

/** 租户预算（PUT / GET /tenants/:tid/budgets） */
export const TenantBudget = z.object({ monthlyLimitMicroUsd: z.number().int().nullable(), perAgentDefaultMicroUsd: z.number().int().nullable(), alertAtPercent: z.number().int() });

/** 充值单（前端扫码 / 跳转后轮询状态）；amount 为币种最小单位（USD 美分 / CNY 分） */
export const Payment = z.object({
  id: z.string(),
  tenantId: TenantId,
  provider: z.string(),
  amount: z.number().int().positive(),
  currency: Currency,
  /** 下单时按汇率折算的入账金额 */
  amountMicroUsd: z.number().int(),
  status: z.enum(["pending", "succeeded", "failed"]),
  failReason: z.string().nullable(),
  createdAt: Timestamp,
  completedAt: Timestamp.nullable(),
});
export type Payment = z.infer<typeof Payment>;

export const Listing = z.object({
  id: ListingId,
  tenantId: TenantId,
  templateId: TemplateId,
  title: LocalizedText,
  description: LocalizedText,
  role: z.string(),
  pricing: z.object({ model: z.enum(["per_rental", "per_day", "free"]), priceMicroUsd: z.number().int().nonnegative() }),
  status: z.enum(["draft", "pending_review", "published", "suspended"]),
  rating: z.number().min(0).max(5).nullable(),
  rentals: z.number().int().nonnegative(),
  createdAt: Timestamp,
});
export type Listing = z.infer<typeof Listing>;

/** 商品评价（每个租用过的租户一条） */
export const ListingReview = z.object({
  rating: z.number().int().min(1).max(5),
  comment: z.string(),
  /** 评价人的显示名 */
  reviewer: z.string(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type ListingReview = z.infer<typeof ListingReview>;

export const Rental = z.object({
  id: RentalId,
  listingId: ListingId,
  tenantId: TenantId,
  projectId: ProjectId,
  agentId: AgentId,
  startsAt: Timestamp,
  endsAt: Timestamp.nullable(),
  priceMicroUsd: z.number().int().nonnegative(),
  rentedBy: UserId,
});
export type Rental = z.infer<typeof Rental>;

// ───────────── 提问 / 通知（P2–P3） ─────────────

export const HumanQuestion = z.object({
  id: z.string(),
  projectId: ProjectId,
  agentId: AgentId,
  question: z.string(),
  options: z.array(z.string()).default([]),
  urgent: z.boolean(),
  status: z.enum(["open", "answered", "expired"]),
  answer: z.string().nullable(),
  createdAt: Timestamp,
});
export type HumanQuestion = z.infer<typeof HumanQuestion>;

/** 通知类别（与 NotificationPrefs 的键一致） */
export const NotificationCategory = z.enum(["approval", "question", "mention", "task_done", "pr_review", "asset_ready", "meeting", "budget", "agent_error"]);
export type NotificationCategory = z.infer<typeof NotificationCategory>;

export const Notification = z.object({
  id: z.string(),
  /** 类别（决定偏好 push / inbox / off；前端按它分组 / 选图标）。控制面总是返回；类型上可选以兼容旧的构造方（mock 等） */
  category: NotificationCategory.optional(),
  code: z.string(),
  params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])),
  link: z.string().nullable(),
  read: z.boolean(),
  createdAt: Timestamp,
});
export type Notification = z.infer<typeof Notification>;

export const NotificationPrefs = z.partialRecord(NotificationCategory, z.enum(["push", "inbox", "off"]));

/** 推送设备（不返回 token：webpush 订阅里含推送密钥） */
export const Device = z.object({
  id: z.string(),
  platform: z.enum(["webpush", "apns", "fcm"]),
  locale: z.string(),
  createdAt: Timestamp,
  updatedAt: Timestamp,
});
export type Device = z.infer<typeof Device>;
